import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getCookie,
  setCookie,
  deleteCookie,
  isTokenExpired,
  COOKIE_NAMES
} from '../utils/cookies'
import { setApiToken } from '../services/api/config'

interface AuthContextType {
  isAuthenticated: boolean
  adminEmail: string | null
  token: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [adminEmail, setAdminEmail] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Check token validity
  const checkTokenValidity = () => {
    const storedToken = getCookie(COOKIE_NAMES.AUTH_TOKEN)
    
    if (!storedToken) {
      setIsAuthenticated(false)
      setToken(null)
      setAdminEmail(null)
      return false
    }

    if (isTokenExpired(storedToken)) {
      // Token expired, clear auth state
      deleteCookie(COOKIE_NAMES.AUTH_TOKEN)
      setIsAuthenticated(false)
      setToken(null)
      setAdminEmail(null)
      navigate('/admin/login')
      return false
    }

    return true
  }

  // Initialize auth state from cookies
  useEffect(() => {
    const storedToken = getCookie(COOKIE_NAMES.AUTH_TOKEN)
    
    if (storedToken && !isTokenExpired(storedToken)) {
      // Decode token to get email
      try {
        const payload = JSON.parse(atob(storedToken.split('.')[1]))
        setToken(storedToken)
        setAdminEmail(payload.email)
        setIsAuthenticated(true)
        setApiToken(storedToken)
      } catch (error) {
        console.error('Failed to decode token:', error)
        deleteCookie(COOKIE_NAMES.AUTH_TOKEN)
      }
    }

    // Set up periodic token check (every 5 minutes)
    checkIntervalRef.current = setInterval(checkTokenValidity, 5 * 60 * 1000)

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
      }
    }
  }, [])

  const login = async (email: string, password: string) => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Important for cookies
      body: JSON.stringify({ email, password })
    })

    if (!response.ok) {
      throw new Error('Invalid credentials')
    }

    const data = await response.json()
    const { token: authToken, expiresAt } = data.data

    // Set cookie on frontend as backup (server should set HttpOnly cookie)
    const expires = new Date(expiresAt)
    setCookie(COOKIE_NAMES.AUTH_TOKEN, authToken, { 
      expires,
      secure: window.location.protocol === 'https:',
      sameSite: 'strict'
    })
    
    setToken(authToken)
    setAdminEmail(email)
    setIsAuthenticated(true)
    setApiToken(authToken)
  }

  const logout = async () => {
    try {
      // Call logout endpoint to clear server-side cookie
      await fetch(`${import.meta.env.VITE_API_URL}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include'
      })
    } catch (error) {
      console.error('Logout error:', error)
    }
    
    // Clear client-side state and cookies
    deleteCookie(COOKIE_NAMES.AUTH_TOKEN)
    
    setToken(null)
    setAdminEmail(null)
    setIsAuthenticated(false)
    setApiToken(null)

    // Clear interval
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current)
    }
    
    navigate('/admin/login')
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, adminEmail, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}